import queryString from 'qs';
import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _pick from 'lodash/pick';
import { RouteComponentProps } from 'react-router';
import { AppName, ValueOf } from 'sl-api-connector/types';

import ReduxStore from 'src/types/store/reduxStore';
import { STACKLINE_SUPER_USER_VALIDATOR_REGEX } from './validators';
import { panic } from 'src/utils/mixpanel';
import { factory as splitFactory, store } from 'src/main';
import { IBrowserClient } from '@splitsoftware/splitio/types/splitio';
import React, { useMemo, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import _get from 'lodash/get';
import { MetricFormatterFn, UseMetricFormatterOptions } from 'src/utils/Hooks';
import convertMetricToDisplayValue from 'src/components/EntityGrid/gridUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { EntityPathname } from 'src/types/application/types';
import { PARENT_PLATFORMS } from 'src/store/modules/parentPlatform/platformUtils';

const BEACON_SPLIT_IO_TREATMENT = 'beacon-pro-features-by-retailer-id';
const ADVISOR_SPLIT_IO_TREATMENT = 'advisor_feature_flag_collections';

/**
 * This function will return an instance of `AppName` which represents the app
 * "currently" active.
 */

export function getAppName() {
  const hostName = window.location.hostname;
  if (/ad-?(.*)\.stackline\.com/.test(window.location.hostname)) {
    return AppName.Advertising;
  } else if (
    hostName.includes('beacon') ||
    window.location.href.indexOf('/beacon') > 0 ||
    window.location.href.indexOf('/jarvis') > 0 ||
    window.location.href.indexOf('#/b/') > 0
  ) {
    return AppName.Beacon;
  } else if (hostName.includes('omni')) {
    return AppName.Omni;
  } else if (hostName.includes('discover')) {
    return AppName.Discover;
  } else if (hostName.includes('enterprise-sso') || hostName.includes('signin')) {
    // Check referrer parameter to determine app branding for SSO login page.
    // Defaults to 'Advisor' since the SSO host is primarily used by advisor
    // users and the referrer is not reliably persisted through the Cognito
    // OAuth redirect. Matches both the canonical `signin.stackline.com` and
    // the legacy `enterprise-sso.stackline.com` alias. Uses `.includes()`
    // (not the exact-match `isSsoHost` helper) — see the import-time note
    // at the top of this file for why we cannot import here.
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('referrer');

    if (referrer) {
      // Normalize referrer by removing suffixes like -localhost, -dev, -alpha
      const normalizedReferrer = referrer.toLowerCase().replace(/-localhost|-dev|-alpha/g, '');

      switch (normalizedReferrer) {
        case 'atlas':
          return AppName.Atlas;
        case 'beacon':
          return AppName.Beacon;
        case 'omni':
          return AppName.Omni;
        case 'discover':
          return AppName.Discover;
        case 'admanager':
          // TODO: sl-api-connector 0.9.73 declares `AppName.AdManager` in its
          // .d.ts but is missing it from the runtime enum, so `AppName.AdManager`
          // is `undefined` at runtime. Returning the literal string until a new
          // sl-api-connector release with `AdManager = "admanager"` ships and
          // package.json is bumped — then swap this back to `AppName.AdManager`.
          return 'AdManager';
        case 'advisor':
        default:
          return 'Advisor';
      }
    }
    return 'Advisor';
  }

  return AppName.Atlas;
}

export function getSearchAppName() {
  if (
    (window.location.href.indexOf('/beacon') > 0 ||
      window.location.href.indexOf('/jarvis') > 0 ||
      window.location.href.indexOf('#/b/') > 0) &&
    window.location.search.indexOf('tab=advertising') > 0
  ) {
    return AppName.Advertising;
  }
  return getAppName();
}

export const isOmni = getAppName() === AppName.Omni;
export const isDrive = getAppName() === AppName.Advertising;
export const isDriveOrBeacon = isDrive || getAppName() === AppName.Beacon;

export const getAppDisplayName = (appName: AppName | 'Advisor' | 'AdManager' | 'EnterpriseSSO') => {
  const displayName = {
    [AppName.Atlas]: 'Atlas',
    [AppName.Beacon]: 'Beacon',
    [AppName.Omni]: 'Omni',
    [AppName.Advertising]: 'Drive',
    [AppName.Discover]: 'Discover',
    Advisor: 'Advisor',
    AdManager: 'Ad Manager',
    EnterpriseSSO: 'Sign In'
  }[appName as AppName];

  if (!displayName) {
    return panic(`Tried to get display name for invalid app name: "${appName}"`);
  }

  return displayName;
};

const colors = {
  green: '#5CC4BE',
  red: '#D85565'
};

export const getAppStage = () => {
  if (__DEV__ || __TEST__) {
    return 'dev';
  }

  const stage = /(?:atlas|beaconpro|beacon|ad|omni|discover)-?(.*)\.stackline\.com/g.exec(
    window.location.host.replace('-pro.', '.').replace('-redesign.', '.')
  );

  return !!stage && _isEmpty(stage[1]) ? 'prod' : 'dev';
};

export const getAppDataFrequency = (appName: AppName) => {
  const appDataFrequency = {
    [AppName.Atlas]: 'weekly',
    [AppName.Beacon]: 'weekly',
    [AppName.Omni]: 'weekly',
    [AppName.Discover]: 'weekly',
    [AppName.Advertising]: 'daily'
  }[appName as AppName];

  if (appDataFrequency) {
    return appDataFrequency;
  }
  return 'weekly';
};

export function isSuperUser(email: string) {
  return STACKLINE_SUPER_USER_VALIDATOR_REGEX.test(email);
}

export function getAppDefaultParams(location?: RouteComponentProps['location']) {
  let queryParams = {};
  if (location) {
    queryParams = queryString.parse(location.search, { ignoreQueryPrefix: true, arrayLimit: 100 });
  }
  if (getAppName() === AppName.Atlas || getAppName() === AppName.Discover) {
    return { ...queryParams, tab: 'scorecard', subtab: 'all' };
  }
  if (isDrive) {
    return { ...queryParams, tab: 'adManager', subtab: 'keyMetrics' };
  }
  //  temporarily routing to price page
  if (getAppName() === AppName.Omni) {
    return { ...queryParams, tab: 'scorecard', subtab: 'all' };
  }
  return { tab: 'sales', subtab: 'keymetrics', ...queryParams };
}

/**
 * Returns true if the provided `emailAddress` belongs to a Stackline user.
 *
 * @param {string} emailAddress The email address of the user
 */
export const isStacklineUser = (emailAddress: string) => /.+@stackline.com/.test(emailAddress);

/**
 * Returns true if the provided `emailAddress` belongs to a Stackline or Piwheel superuser.
 *
 * @param {string} emailAddress The email address of the user
 */
export const isReclassifyUser = (emailAddress: string) =>
  /.+superuser@stackline.com|.+superuser@piwheel.com/.test(emailAddress);

/**
 * Returns `true` if the currently logged in user should be presented with the reclassify interface and `false`
 * otherwise.
 *
 * @param {object} app `app` from Redux
 * @param {object} user `user` from Redux
 * @param {object} location `location` from `withRouter`
 */
export const shouldShowReclassify = (
  app: ReduxStore['app'],
  user: ReduxStore['user'],
  location: RouteComponentProps['location']
) => {
  const {
    session: { email }
  } = user;
  const queryParams = queryString.parse(location.search, { ignoreQueryPrefix: true, arrayLimit: 100 });

  // Do not show the reclassify UI in any of these situations:
  // 1) We're not on Atlas
  // 2) We're not on the search page
  // 3) We're not on the segment edit route
  // 4) We're on production

  if (
    // app.name !== AppName.Atlas ||
    !location.pathname.includes('/search') ||
    queryParams.entityType !== 'segment' ||
    getAppStage() === 'prod'
  ) {
    return false;
  }

  // If all of the previous conditions pass (meaning that we're on the search page, in the segment view, and not on
  // production), we display the reclassify UI if we're a Stackline user or a piwheel superuserr.
  return isReclassifyUser(email);
};

/**
 * Builds the base URL for making API requests
 */
export const buildBaseTarget = () => {
  const { hostname } = window.location;
  let apiEndpoint = '';

  // Drive (ad-*) keeps same-origin to avoid CORS preflights — see legacy
  // Cloudfront constraint noted in the original implementation.
  if (hostname.startsWith('ad')) {
    apiEndpoint = hostname;
  } else if (hostname.includes('localhost')) {
    // Route all localhost stackline subdomains (atlas, beacon, omni, etc.,
    // and enterprise-sso) through api-localhost.stackline.com so V1 session
    // cookies set on the API host are visible to every app — same pattern
    // as production where atlas.stackline.com calls api.stackline.com.
    apiEndpoint = 'api-localhost.stackline.com';
  } else if (hostname.includes('-dev')) {
    // Explicit dev stage handling
    apiEndpoint = 'api-dev.stackline.com';
  } else if (hostname.indexOf('stackline.com') >= 0) {
    apiEndpoint = hostname.replace(/atlas|beacon-pro|beaconpro|beacon|ad|omni|discover/, 'api');
  } else {
    apiEndpoint = 'api.stackline.com';
  }

  return apiEndpoint;
};

export const buildLlmCrawlerPath = (endpoint: string) => {
  return `api/llm-crawler/${endpoint}`;
};

/**
 * Fetches a piece of metadata from the API via `AdvancedSearch`, returning a `Promise` that resolves to it.
 *
 * @param {string} appName
 * @param {string} name The `name` and `searchType` fields of the created request
 * @param {object?} requestOverrides Additional props to add to the request object made to the server
 */
export const fetchUserMetadata = (appName: ReduxStore['app']['name'], name: string, requestOverrides: {} = {}) => {
  const request = [
    {
      name,
      pageNumber: 1,
      pageSize: 20000,
      doAggregation: false,
      returnDocuments: true,
      searchBy: 'parent',
      searchType: name,
      aggregations: null,
      ...requestOverrides
    }
  ];

  return axios.post(`/api/${appName}/AdvancedSearch?_id=fetchUserMetadata`, request);
};

export const getEntityUrlInApp = (app: { name: string; stage: string }, type: string, id: string | number) => {
  const baseURL = `https://${app.name}${app.stage !== 'prod' ? `-${app.stage}` : ''}.stackline.com/${type}/${id}`;
  return baseURL;
};

/**
 * Returns true if the selected retailer is Instacart US or Instacart Canada
 * @param retailerId The ID of the retailer selected in the nav bar
 * @deprecated Please use checkInstacartRetailer() instead
 */
export const isInstacart = (retailerId: ReduxStore['retailer']['id']) => {
  // 63 is Instacart US
  // TODO add '100' when Instacart Canada releases optimization
  return ['63'].includes(retailerId);
};

/**
 * Returns true if the user is currently on the
 * AMC connect dashboard
 */
export const isConnectDashboard = () => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('tab') === 'connectDashboard';
};

/**
 * Not all pages should show the previous period dropdown in the header.
 * For now, just the AMC Connect page should not show it but add
 * additional logic here as necessary.
 */
export const shouldShowComparisonTimePeriod = () => {
  return !isConnectDashboard();
};

/**
 * Split IO to determine if the fetchAllAdCampaigns() call should use the platformType filter
 */
export const shouldFetchAllCampaignsWithPlatformTypeFilter = (
  beaconClientId: number,
  userEmail: string,
  appStage: string
) => {
  const SPLIT_IO_TREATMENT = 'fetch_adcampaigns_with_platform_type_filter';

  const splitClient: IBrowserClient = splitFactory.client();
  const should =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      beaconClientId,
      userEmail,
      host: window.location.hostname
    }) === 'on';

  if (appStage !== 'prod') {
    console.log(`${SPLIT_IO_TREATMENT}: ${should}`);
  }
  return should;
};

/**
 * Split IO rule to determine whether or not to render the new Beacon pages.
 * @param userEmail
 * @param appStage
 * @returns boolean (true): show new beacon pages, (false): show normal Beacon pages
 */
export const shouldShowNewBeacon = () => {
  return getAppName() === AppName.Beacon;
};

export const getAppSubscriptionTier = () => {
  const appName = getAppName();

  const retailerId = _get(store.getState(), ['retailer', 'id']);

  const subscriptionsByRetailers = _get(store.getState(), ['user', 'config', 'subscriptionsByRetailers']);

  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);
  const splitClient: IBrowserClient = splitFactory.client();
  const forceBeaconPro =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'ForceBeaconPro',
      retailerId,
      beaconClientId,
      userEmail
    }) === 'on';

  if (
    (appName === AppName.Beacon &&
      subscriptionsByRetailers[`${retailerId}`] &&
      subscriptionsByRetailers[`${retailerId}`].BeaconProSubscription === 'true') ||
    (forceBeaconPro && getAppName() === AppName.Beacon)
  ) {
    return 'pro';
  }
  if (
    subscriptionsByRetailers[`${retailerId}`] &&
    subscriptionsByRetailers[`${retailerId}`].PrimeSubscription === 'true'
  ) {
    return 'prime';
  }
  return 'basic';
};

/**
 * Split IO rule to determine whether or not to render the new Beacon pages.
 * @param userEmail
 * @param appStage
 * @returns boolean (true): show new beacon pages, (false): show normal Beacon pages
 */
export const shouldShowNewAtlas = () => {
  const isAtlas = getAppName() === AppName.Atlas;

  if (!isAtlas) {
    return false;
  }

  // Notes: https://stackline.atlassian.net/browse/GPT-537
  // Some users maybe in incompatible network with Split.io, we will fallback to enabled for now
  // The best solution in long term is having fallback through C# API proxy to Split.io
  try {
    const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
    const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);

    const SPLIT_IO_TREATMENT = 'atlas_redesign_split';

    const splitClient: IBrowserClient = splitFactory.client();
    const treatment = splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      userEmail,
      host: window.location.hostname,
      beaconClientId,
      pathname: window.location.pathname,
      appStage: getAppStage()
    });

    if (treatment === 'off') {
      return false;
    }

    return true;
  } catch (e) {
    // Default to enabled — only superuser accounts are served 'off'
    return true;
  }
};

/**
 * Common method for Split.io feature flag evaluation using ADVISOR_SPLIT_IO_TREATMENT.
 * This is used to check features under the advisor_feature_flag_collections treatment.
 * @param feature - The feature name to check
 * @param userEmail - The user's email address
 * @returns boolean indicating if the feature treatment is 'on'
 */
const getAdvisorFeatureTreatment = (feature: string, userEmail: string): boolean => {
  const splitClient: IBrowserClient = splitFactory.client();

  return (
    splitClient.getTreatment(ADVISOR_SPLIT_IO_TREATMENT, {
      feature_name: feature,
      userEmail
    }) === 'on'
  );
};

export const shouldUseAtlasLLMQuestions = (): boolean => {
  if (getAppName() !== AppName.Atlas) {
    return false;
  }

  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  return getAdvisorFeatureTreatment('atlas_advisor_llm_questions', userEmail);
};

/**
 * Checks if the Atlas chart-to-Advisor integration should be enabled.
 * Shows "Ask Advisor" button on charts when enabled.
 * Only applicable for Atlas app users with the atlas-chart-integration feature enabled.
 * @returns boolean indicating if chart integration should be shown
 */
export const shouldShowAtlasChartIntegration = (): boolean => {
  if (getAppName() !== AppName.Atlas) {
    return false;
  }

  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  return getAdvisorFeatureTreatment('atlas-chart-integration', userEmail);
};

/**
 * Checks if the Beacon Advisor integration should be enabled.
 * Shows "Ask Advisor" button in the search bar and chart dropdowns when enabled.
 * Only applicable for Beacon app users with the beacon-advisor-connetion-flag feature enabled.
 * Evaluates against atlas-v2-feature-collections (not advisor_feature_flag_collections).
 * @returns boolean indicating if Beacon Advisor should be shown
 */
export const shouldShowBeaconAdvisor = (): boolean => {
  if (getAppName() !== AppName.Beacon) {
    return false;
  }

  const state = store.getState();
  const userEmail = _get(state, ['user', 'session', 'email'], '');
  const divisionId = _get(state, ['user', 'session', 'divisionId'], '');
  const clientId = _get(state, ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(state, ['retailer', 'id']);
  const splitClient: IBrowserClient = splitFactory.client();

  return (
    splitClient.getTreatment('atlas-v2-feature-collections', {
      feature: 'beacon-advisor-connetion-flag',
      email: userEmail,
      clientId,
      host: window.location.hostname,
      retailerId,
      divisionId
    }) === 'on'
  );
};

/**
 * Determines the Advisor host based on current Atlas environment.
 * Used for opening Advisor in new tab and future Backend Context API.
 * - atlas-localhost.stackline.com → zenith-localhost.stackline.com
 * - atlas-dev.stackline.com → advisor-dev.stackline.com
 * - atlas.stackline.com → advisor.stackline.com
 */
export const getAdvisorHost = (): string => {
  const hostname = window.location.hostname;

  if (hostname.includes('localhost')) {
    return 'zenith-localhost.stackline.com:8443';
  }
  if (getAppStage() === 'prod') {
    return 'advisor.stackline.com';
  }
  return 'advisor-dev.stackline.com';
};

export const redirectAdvertising = (beaconClientId: string) => {
  const splitClient: IBrowserClient = splitFactory.client();
  const showAdvertisingRules =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'AdvertisingRedirect',
      beaconClientId: beaconClientId ?? 'true',
      userEmail: 'none',
      retailerId: 'none'
    }) === 'on';

  return showAdvertisingRules;
};

export const isBeaconCriteo = () => {
  const retailerId = store.getState().retailer.id;
  const availablePlatforms = store.getState().parentPlatform?.availablePlatforms;
  const criteoRetailers = availablePlatforms
    ?.find((platform) => platform.id === 'Criteo')
    ?.retailers.map((x) => x.extendedAttributes.retailerId);

  return criteoRetailers?.includes(Number(retailerId));
};

export const shouldShowAlertV1DevMode = () => {
  const splitClient: IBrowserClient = splitFactory.client();
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');

  const showAlertV1DevMode =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'AlertV1Dev',
      userEmail
    }) === 'on'; // This will be used for both atlas and beacon

  return showAlertV1DevMode;
};

export const shouldShowAdvertisingIneligible = () => {
  const splitClient: IBrowserClient = splitFactory.client();
  const retailerId = _get(store.getState(), ['retailer', 'id'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');

  const showAdvertisingIneligibleTab =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'AdvertisingIneligible',
      beaconClientId: beaconClientId ?? 'true',
      userEmail,
      retailerId
    }) === 'on' && getAppName() === AppName.Beacon;

  return showAdvertisingIneligibleTab;
};

/**
 * Common method for Split.io feature flag evaluation using BEACON_SPLIT_IO_TREATMENT treatment
 * @param feature - The feature name to check in the Split.io treatment
 * @returns boolean indicating if the feature treatment is 'on'
 */
const getBeaconProFeatureTreatment = (feature: string): boolean => {
  const splitClient: IBrowserClient = splitFactory.client();
  const retailerId = _get(store.getState(), ['retailer', 'id'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');

  return (
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature,
      beaconClientId: beaconClientId ?? 'true',
      userEmail,
      retailerId
    }) === 'on'
  );
};

export const showAtlasPresentationBuilder = () => {
  return getAtlasTreatment('presentationBuilder') === 'on' && getAppName() === AppName.Atlas;
};

export const showContentGenerationProjects = () => {
  return getBeaconProFeatureTreatment('ContentGenerationProjects') && getAppName() === AppName.Beacon;
};

export const showContentSyndication = () => {
  return getBeaconProFeatureTreatment('ContentSyndication') && getAppName() === AppName.Beacon;
};

export const showBeaconAlertV1 = () => {
  return getBeaconProFeatureTreatment('AlertV1') && getAppName() === AppName.Beacon;
};

export const showAtlasAlertV1 = () => {
  return getAtlasTreatment('AlertV1') === 'on' && getAppName() === AppName.Atlas;
};

export const shouldShowAdvertisingPortolio = () => {
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);

  const splitClient: IBrowserClient = splitFactory.client();
  const retailerId = _get(store.getState(), ['retailer', 'id'], '');

  const showAdvertisingRules =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'AdvertisingPortfolio',
      beaconClientId,
      userEmail,
      retailerId
    }) === 'on' && getAppName() === AppName.Beacon;
  return showAdvertisingRules;
};

/**
 * Controls rollout of the new Beacon Pro topics and topic groups features
 */
export const shouldShowBeaconProTopics = () => {
  return (
    (getAppSubscriptionTier() === 'pro' || getAppSubscriptionTier() === 'prime') && getAppName() === AppName.Beacon
  );
};

export const shouldShowPromotionsAdjustment = () => {
  return getAppSubscriptionTier() === 'pro';
};

export const getContentV3DisplayConfig = () => {
  const retailerId = _get(store.getState(), ['retailer', 'id'], '');
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);

  const splitClient: IBrowserClient = splitFactory.client();
  const showContentV3 =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'contentV3',
      retailerId,
      beaconClientId,
      userEmail
    }) === 'on';

  return { requireSubscription: false, showContentV3 };
};

export const getContentV2DisplayConfig = () => {
  if (getAppSubscriptionTier() !== 'pro') {
    return { requireSubscription: true, showContentV2: true };
  }
  const retailerId = _get(store.getState(), ['retailer', 'id'], '');
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);

  const splitClient: IBrowserClient = splitFactory.client();
  const showContentV2 =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'ContentV2',
      retailerId,
      beaconClientId,
      userEmail
    }) === 'on';
  return { requireSubscription: false, showContentV2 };
};

/**
 * Determines which content version the current user should see
 * @returns 'content' | 'contentV2' | 'contentV3'
 */
export const getCurrentContentVersion = (): 'content' | 'contentV2' | 'contentV3' => {
  const subscriptionTier = getAppSubscriptionTier();

  if (subscriptionTier !== 'pro') {
    return 'content';
  }

  const { showContentV3 } = getContentV3DisplayConfig();
  const { showContentV2 } = getContentV2DisplayConfig();

  if (showContentV3) {
    return 'contentV3';
  }

  if (showContentV2) {
    return 'contentV2';
  }

  return 'content';
};

// TODO: Move this to retailer config when we have the ability to do so
export const shouldShowRatingsAndReviewsV2 = () => {
  const retailerId = _get(store.getState(), ['retailer', 'id'], '');

  const splitClient: IBrowserClient = splitFactory.client();
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);

  const showReviews =
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'ReviewsV2',
      retailerId,
      beaconClientId,
      userEmail
    }) === 'on';

  return showReviews && getAppName() === AppName.Beacon;
};

/**
 * Split IO rule to determine whether or not to use Criteo feature.
 * @param userEmail
 * @param appStage
 * @returns boolean (true): show new beacon pages, (false): show normal Beacon pages
 */
export const shouldShowCriteo = () => {
  return getAppName() === AppName.Advertising || window.location.search.indexOf('tab=advertising') > 0;
};

const getParentPlatform = () => {
  const params = queryString.parse(window.location.search, { ignoreQueryPrefix: true, arrayLimit: 100 });
  const parentPlatform = _get(params, 'pp', '');
  return parentPlatform;
};

export const isCriteo = () => {
  return getParentPlatform() === PARENT_PLATFORMS.CRITEO;
};

export const shouldShowIntercomChat = () => {
  const SPLIT_IO_TREATMENT = 'enterprise-intercom';
  const splitClient: IBrowserClient = splitFactory.client();
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const clientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(store.getState(), ['retailer', 'id']);

  const should =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      feature: 'IntercomChat',
      email: userEmail,
      clientId,
      host: window.location.hostname,
      retailerId
    }) === 'on';

  return should;
};
/**
 * Split IO rule to determine whether or not to 1P Retail Sales For Atlas.
 * @param userEmail
 * @param host
 * @returns boolean (true): show 1P retail sales, (false): don't show 1P retail sales
 */
export const shouldShowOnePRetailSales = () => {
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const clientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(store.getState(), ['retailer', 'id']);
  const SPLIT_IO_TREATMENT = 'atlas-onep-split';
  const splitClient: IBrowserClient = splitFactory.client();
  const should =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      email: userEmail,
      clientId,
      host: window.location.hostname,
      retailerId
    }) === 'on' && getAppName() === AppName.Atlas;
  return should;
};

/**
 * @deprecated Use getTrafficV2Treatment instead
 */
export const shouldShowTrafficV2 = () => {
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const clientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(store.getState(), ['retailer', 'id']);
  const SPLIT_IO_TREATMENT = 'atlas-v2-feature-collections';
  const splitClient: IBrowserClient = splitFactory.client();
  const should =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      feature: 'TrafficV2',
      email: userEmail,
      clientId,
      host: window.location.hostname,
      retailerId
    }) === 'on' && getAppName() === AppName.Atlas;

  return should;
};

// Branded Unbranded for atlas traffic v2
export const shouldShowBrandUnbranded = () => {
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const divisionId = _get(store.getState(), ['user', 'session', 'divisionId'], null);
  const SPLIT_IO_TREATMENT = 'atlas-v2-feature-collections';
  const clientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(store.getState(), ['retailer', 'id']);
  const splitClient = splitFactory.client();

  const should =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      feature: 'BrandedKeyword',
      email: userEmail,
      clientId,
      divisionId,
      host: window.location.hostname,
      retailerId
    }) === 'on' && getAppName() === AppName.Atlas;

  return should;
};

const getAtlasTreatment = (featureName: string): 'off' | 'on' | 'both' => {
  const state = store.getState();
  const userEmail = _get(state, ['user', 'session', 'email'], '');
  const divisionId = _get(state, ['user', 'session', 'divisionId'], '');
  const clientId = _get(state, ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(state, ['retailer', 'id']);
  const host = window.location.hostname;
  const splitClient = splitFactory.client();

  let treatment = splitClient.getTreatment('atlas-v2-feature-collections', {
    feature: featureName,
    email: userEmail,
    clientId,
    host,
    retailerId,
    divisionId
  });

  if (getAppName() !== AppName.Atlas) {
    treatment = 'off';
  }

  return treatment as 'off' | 'on' | 'both';
};

export const getAtlasProductTagsTreatment = (): 'off' | 'on' | 'both' => getAtlasTreatment('AtlasProductTags');
export const getAtlasSalesV2Treatment = (): 'off' | 'on' | 'both' => getAtlasTreatment('AtlasSalesV2');
export const getTrafficV2Treatment = (): 'off' | 'on' | 'both' => getAtlasTreatment('TrafficV2');
export const getAtlasConversionV2Treatment = (): 'off' | 'on' | 'both' => getAtlasTreatment('AtlasConversionV2');

export const shouldShowPresentations = () => {
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const clientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(store.getState(), ['retailer', 'id']);
  const SPLIT_IO_TREATMENT = 'atlas-v2-feature-collections';
  const splitClient: IBrowserClient = splitFactory.client();
  const should =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      feature: 'Presentations',
      email: userEmail,
      clientId,
      host: window.location.hostname,
      retailerId
    }) === 'on' && getAppName() === AppName.Atlas;

  return should;
};

export const shouldShowMicroTrends = () => {
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const clientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const retailerId = _get(store.getState(), ['retailer', 'id']);
  const SPLIT_IO_TREATMENT = 'atlas-v2-feature-collections';
  const splitClient: IBrowserClient = splitFactory.client();
  const should =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      feature: 'MicroTrends',
      email: userEmail,
      clientId,
      host: window.location.hostname,
      retailerId
    }) === 'on' && getAppName() === AppName.Atlas;

  return should;
};

/**
 * Returns true if we should fetch all rows for
 * an entity, including those without metrics
 */
export const shouldFetchAllSRGRows = (entityType: string, groupByField: string) => {
  return entityType === 'adTarget' && groupByField === 'stacklineSku';
};

/**
 * Simple function to determine if client is Apple
 * @returns boolean (true): is Apple client, (false): is not Apple client
 */
export const isApple = () => {
  const beaconClientId = Number(_get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], -1));

  return [162].includes(beaconClientId);
};

/**
 * Log the message to console, if not running on production environment
 */
export const logDev = (appStage: string, message: string) => {
  if (appStage !== 'prod') {
    console.log(message);
  }
};

/**
 * Returns true if the page uses secondary retailers instead
 * of the normal ones for the app
 */
export const usesSecondaryRetailers = (): boolean => {
  return isConnectDashboard();
};

/**
 * Returns true if the page uses a custom main time period
 * dropdown instead of the app defaults
 */
export const usesCustomTimePeriods = (): boolean => {
  return isConnectDashboard();
};

/**
 * Parses two search strings and determines if there are any differences
 * between any of the query strings. If given `keysToWatch`, it will only
 * return true if there are differences between the given keys.
 */
export const queryParamsEqual = (search1: string, search2: string, keysToWatch?: string[]) => {
  const params1 = new URLSearchParams(search1);
  const params2 = new URLSearchParams(search2);

  const params1Obj = Object.fromEntries([...params1.entries()]);
  const params2Obj = Object.fromEntries([...params2.entries()]);

  const keys = keysToWatch || [...new Set([...params1.keys(), ...params2.keys()])];

  return _isEqual(_pick(params1Obj, keys), _pick(params2Obj, keys));
};

export default {
  colors,
  getAppName
};

type UseMemoParams = Parameters<typeof React.useMemo>;
type MemoCallback = UseMemoParams[0];
type DependencyList = UseMemoParams[1];

/**
 * Uses useDeepCompareEffect to create a memoized value that is only
 * updated when the objects in its dependencies array are not
 * deeply equal, even if the references change.
 */
export function useDeepCompareMemo(callback: MemoCallback, dependencies: DependencyList) {
  const rawMemoizedValue = useMemo(callback, dependencies);
  const [memoizedValue, setMemoizedValue] = useState(rawMemoizedValue);

  useDeepCompareEffect(() => {
    setMemoizedValue(rawMemoizedValue);
  }, [rawMemoizedValue]);

  return memoizedValue;
}

export function downloadFile(url: string, filename: string) {
  // Validate input
  if (!url || !filename || !url.startsWith('http') || !url.includes('.')) {
    throw new Error('Invalid input. Please provide a valid URL and filename.');
  }

  // Extract the file extension from the URL
  const extension = url
    .slice(url.lastIndexOf('/') + 1)
    .split('.')[1]
    .split('?')[0];

  // Add filetype to filename if it's not present
  filename = filename.endsWith(`.${extension}`) ? filename : `${filename}.${extension}`;

  fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.blob();
    })
    .then((blob) => {
      const _url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = _url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(_url);
    })
    .catch((e) => console.error(e));
}

/**
 * Formats a metric given
 */
export const formatMetric: MetricFormatterFn = (
  value: number,
  metricType: ValueOf<typeof METRICTYPE>,
  options: UseMetricFormatterOptions
) => {
  const { retailer } = store.getState();

  return convertMetricToDisplayValue(
    retailer,
    value,
    metricType,
    retailer.currencySymbol,
    typeof options.showFullValue !== 'undefined' ? options.showFullValue : true,
    options
  );
};

interface EntityParam {
  path: EntityPathname;
  routeParam?: string | number | null;
}

const getNewPath = (entityPath: EntityParam) => {
  const { path, routeParam } = entityPath;
  return `${path}${routeParam || ''}`;
};
export interface UpdateUrlQueryParams {
  [key: string]: string | string[] | null;
}
/**
 * Given a set of query parameters and values, return a new
 * path with the update keys and values in the URL.
 * To delete a param, pass the key with a value of null.
 * @param newParamValues An object containing param keys and values to add to the URL. To add multiple values to one key, pass the values as an array.
 * @param entityPath Optional object containing an EntityPathname as a path and an optional routeParam value. i.e: {path: Entitypath.Product, routeParam: SKU12345}
 * @param paramsOnly Optional boolean to return only the query params string without any additional information
 */
export const updateUrlQueryParams = (
  newParamValues: UpdateUrlQueryParams,
  entityPath?: EntityParam,
  paramsOnly = false
) => {
  const params = paramsOnly ? new URLSearchParams() : new URLSearchParams(window.location.search);

  Object.entries(newParamValues).forEach(([key, value]) => {
    // If the value is an array, we can append multiple values under the same param key.
    if (Array.isArray(value)) {
      value.forEach((paramValue) => {
        // If the value is null, we can safely delete the parameter under the given key.
        if (paramValue === null) {
          params.delete(key);
        } else {
          params.append(key, paramValue);
        }
      });
    } else if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  });

  if (paramsOnly) {
    return `&${params.toString()}`;
  }

  // If we're optionally given a path such as EntityPathname.Product with a routeParam (product SKU in this case), we change the path.
  const pathname = entityPath ? getNewPath(entityPath) : window.location.pathname;
  return `${pathname}?${params.toString()}`;
};

export const getQueryParamValue = (key: string, defaultValue?: string): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) || defaultValue;
};

export const checkInstacartRetailer = () => {
  try {
    const retailerId = getQueryParamValue('rid', '');
    return ['63'].includes(retailerId);
  } catch (e) {
    return false;
  }
};

export const calculatePercentChange = (mainMetric: number, comparisonMetric: number): number => {
  // Handle division by 0 cases
  if (comparisonMetric === 0) {
    if (mainMetric === 0) {
      return 0;
    }
    return mainMetric > 0 ? 1000 : -1000;
  }

  if (mainMetric === comparisonMetric) {
    return 0;
  }

  const percentChange =
    Math.abs(comparisonMetric) > 0 ? (mainMetric - comparisonMetric) / Math.abs(comparisonMetric) : 0;

  const minPercent = 1e-6;

  // Super small values get formatted as NaN, so we round up
  if (Math.abs(percentChange) < minPercent) {
    return minPercent * (percentChange < 0 ? -1 : 1);
  }

  return percentChange;
};

/**
 * Performs division on two numbers, returning a default value if the denominator is 0.
 */
export const safeDivide = (numerator: number, denominator: number, defaultIfZero = 0): number => {
  if (denominator === 0) {
    return defaultIfZero;
  }
  return numerator / denominator;
};

interface SafeJSONParseParams {
  value: string;
  defaultValue?: any | null;
}

/**
 * Safely parses a JSON string, returning a default value if the parse fails.
 */
export const safeJSONParse = ({ value, defaultValue = null }: SafeJSONParseParams) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    console.error('Failed to parse JSON: ', e);
    return defaultValue;
  }
};

export const shouldShowBeaconAdvertising = () => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { isV2Enabled } = require('src/utils/authVersion');
  if (isV2Enabled()) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { currentDivisionHasAppAccess } = require('src/utils/divisionStorage');
    return currentDivisionHasAppAccess('advertising');
  }
  const user = _get(store.getState(), ['user'], '');
  const appSubscriptions = _get(user, ['config', 'appSubscriptions'], []);
  return appSubscriptions.includes('advertising');
};

export const getBaseUrl = () => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete('tab');
  currentUrl.searchParams.delete('subtab');

  return `${currentUrl.pathname}${currentUrl.search}`;
};

export const showContentVariation = () => {
  return getBeaconProFeatureTreatment('contentVariations');
};

export const showContentVariationClient = () => {
  return getBeaconProFeatureTreatment('contentVariationsClient');
};

/**
 * Split IO rule to determine whether to use high sample rate (1.0) for Sentry Session Replay
 * @returns boolean (true): use 1.0 sample rate, (false): use default 0.1 sample rate
 */
export const shouldUseHighSentryReplaySampleRate = () => {
  const SPLIT_IO_TREATMENT = 'sentry-high-sample-rate';
  const splitClient: IBrowserClient = splitFactory.client();
  const retailerId = _get(store.getState(), ['retailer', 'id'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId']);
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  const appStage = getAppStage();

  const shouldUseHighSampleRate =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      retailerId,
      beaconClientId,
      userEmail
    }) === 'on';

  // Log the treatment result in non-prod environments for debugging
  if (appStage !== 'prod') {
    console.log(`${SPLIT_IO_TREATMENT}: ${shouldUseHighSampleRate}`);
  }

  return shouldUseHighSampleRate;
};

/**
 * Feature flag for showing the Shelf tab in primary navigation
 */
export const shouldShowDigitalShelfTab = () => {
  const SPLIT_IO_TREATMENT = 'atlas-digital-shelf';
  const splitClient: IBrowserClient = splitFactory.client();
  const state = store.getState();
  const userEmail = _get(state, ['user', 'session', 'email'], '');
  const beaconClientId = _get(state, ['user', 'config', 'vendor', 'BeaconClientId'], null);
  const divisionId = _get(state, ['user', 'session', 'divisionId'], null);

  const treatment =
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      feature: 'DigitalShelf',
      email: userEmail,
      beaconClientId,
      host: window.location.hostname,
      divisionId
    }) === 'on';

  return treatment;
};

/**
 * Feature flag for enabling user interaction tracking
 * Controls whether to track user mouse movements, clicks, keystrokes, scrolls, etc.
 * This helps with analytics and understanding user behavior patterns.
 */
export const shouldEnableUserTracking = (): boolean => {
  const SPLIT_IO_TREATMENT = 'enterprise_user_interaction_tracking_enabled';
  const splitClient: IBrowserClient = splitFactory.client();
  const state = store.getState();
  const userEmail = _get(state, ['user', 'session', 'email'], '');
  const appStage = getAppStage();

  // Only check if we have a user email
  if (!userEmail) {
    if (appStage !== 'prod') {
      console.log(`[Split.io] ${SPLIT_IO_TREATMENT}: ⏳ waiting for user session`);
    }
    return false; // Default to disabled when no user
  }

  // Only pass email to Split.io
  const shouldTrack = splitClient.getTreatment(SPLIT_IO_TREATMENT, { email: userEmail }) === 'on';

  // Log the treatment result in non-prod environments for debugging
  if (appStage !== 'prod') {
    if (shouldTrack) {
      console.log(`[Split.io] ${SPLIT_IO_TREATMENT}: ✅ ENABLED for user ${userEmail}`);
    } else {
      console.log(`[Split.io] ${SPLIT_IO_TREATMENT}: ❌ DISABLED for user ${userEmail}`);
    }
  }

  return shouldTrack;
};
